import axios, {AxiosError, AxiosResponse} from 'axios';
import {
    JsonRpcRequest,
    JsonRpcResponse
} from './typings/game-server';
import {RpcReceivedError} from './errors/rpc-received-error';
import {isTimeoutError} from './helpers/is-timeout-error';
import {ConnectionTimeoutError} from './errors/connection-timeout-error';
import {ApiLogData} from './utility/api-logging-adapter-interface';
import {ApiMetricData} from './utility/api-metrics-adapter-interface';
import {ProtocolTransformer} from './protocol-transform/protocol-transformer';
import {BaseApiWrapper} from './base-api-wrapper';

export enum Timeout {
    TIMEOUT_5_SECONDS = 5000,
    TIMEOUT_20_SECONDS = 20000,
    TIMEOUT_25_SECONDS = 25000,
}

export enum ErrorCode {
    SERVER_ERROR = 500
}

type JSONRpcId = number|string|null;
type Headers = Record<string, string|number|boolean>;

export abstract class JsonRpcRequestWrapper<REQ, SUCC> extends BaseApiWrapper {
    public url?: string;
    public requestData: JsonRpcRequest<REQ>;

    public timeoutMs: number = Timeout.TIMEOUT_5_SECONDS;
    private transformerAdapter?: ProtocolTransformer<REQ, SUCC>;
    private requestHeaders?: Headers;

    protected constructor(method: string, params: REQ, id: JSONRpcId = null) {
        super();
        this.requestData = {
            id: id,
            jsonrpc: '2.0',
            method: method,
            params: params
        };
    }

    public setTransformerAdapter(adapter: ProtocolTransformer<REQ, SUCC>) {
        this.transformerAdapter = adapter;
    }

    public async run(): Promise<SUCC> {
        const startTime = Date.now();
        let axiosResponse: AxiosResponse;
        if (!this.url) {
            throw new Error('Server request url not set');
        }

        if (!this.url.match(/https?:\/\//)) {
            this.url = `http://${this.url}`;
        }

        this.logRequestData();

        if (!this.transformerAdapter) {
            throw new Error('Transformer adapter is required');
        }

        const requestData = this.transformerAdapter.transformRequest(this.requestData);

        try {
            axiosResponse = await axios.post(this.url, requestData, {
                headers: this.requestHeaders,
                timeout: this.timeoutMs
            });
        } catch (error) {
            // @ts-ignore
            throw this.transformError(error);
        }

        const rawResponseData = axiosResponse.data;
        this.logRawResponseData(rawResponseData);

        const transformedResponse = this.transformerAdapter.transformResponse(rawResponseData);
        this.logResponseData(transformedResponse);

        if (this.isErrorResponse(transformedResponse)) {
            const serverSentError: Error = this.getResponseError(transformedResponse);
            this.logErrorResponse(serverSentError);
            throw serverSentError;
        }

        this.sendMetric('duration', Date.now() - startTime);


        return transformedResponse.result!;
    }

    public setCustomHeaders(headers: Headers) {
        this.requestHeaders = headers;
    }

    // eslint-disable-next-line class-methods-use-this
    protected getResponseError(responseData: JsonRpcResponse<SUCC>): RpcReceivedError {
        if (!responseData.error) {
            throw new Error('Trying to get error from non-error response');
        }

        let errorMessage = responseData.error.message;
        if (!errorMessage) {
            errorMessage = `No error message, failed with code ${responseData?.error?.code ?? 'No code'}`;
        }

        const errorCode = responseData.error.code ?? ErrorCode.SERVER_ERROR;
        const errorData = responseData.error.data;

        return new RpcReceivedError(errorMessage, errorCode, errorData);
    }

    // eslint-disable-next-line class-methods-use-this
    protected isErrorResponse(responseData: JsonRpcResponse<SUCC>): boolean {
        return 'error' in responseData;
    }

    private sendMetric(metric: string, value: number) {
        if (this.metricsAdapter) {
            this.metricsAdapter.writeMetric(this.getApiMetricData(metric, value));
        }
    }

    // eslint-disable-next-line class-methods-use-this
    private transformError(error: Error|AxiosError): Error {
        if ('code' in error && isTimeoutError(error)) {
            return new ConnectionTimeoutError(error.message);
        }

        return error;
    }

    private logRequestData() {
        if (this.loggingAdapter) {
            this.loggingAdapter.logRequest(this.getApiLogData(), this.requestData);
        }
    }

    private logRawResponseData(data: unknown) {
        if (this.loggingAdapter) {
            this.loggingAdapter.logResponse(this.getApiLogData(), data);
        }
    }

    private logResponseData(data: JsonRpcResponse<SUCC>) {
        if (this.loggingAdapter) {
            this.loggingAdapter.logResponse(this.getApiLogData(), data);
        }
    }

    private logErrorResponse(serverSentError: Error) {
        if (this.loggingAdapter) {
            const requestData = this.getApiLogData();
            this.loggingAdapter.logErrorResponse(requestData, serverSentError);
        }
    }

    private getApiLogData(): ApiLogData {
        return {
            method: this.requestData.method,
            url: this.url!
        };
    }

    private getApiMetricData(metricName: string, metricValue: number): ApiMetricData {
        return {
            method: this.requestData.method,
            metricName,
            metricValue,
            url: this.url!
        };
    }
}
